import React, { useCallback, useEffect } from 'react';
import { css } from '@emotion/core';
import { useSelector } from 'react-redux';
import t from 'react-translate';
import { isEmpty } from 'underscore';
import { useFormContext } from 'react-hook-form';

// redux
import { useAppDispatch } from 'redux/store';
import { getFlatCourseAliases } from 'redux/selectors/course';
import { ResponseOption, MultiChoiceMultiAnswerQuestionResponse } from 'redux/schemas/models/quiz';
import { RootState } from 'redux/schemas';
import { getQuestionAnswerFeedback } from 'redux/selectors/quizzes';
import { wrapThunkAction } from 'redux/utils';
import {
  editQuizQuestionOption,
  deleteQuizQuestionOption,
  EditQuizQuestionOptionParams,
  editAnswerFeedback,
} from 'redux/actions/quizzes';
import { getActivityType } from 'redux/selectors/timeline';

import NvIcon from 'shared/components/nv-icon';
import NvFroala from 'froala/components/nv-froala';
import { AngularServicesContext } from 'react-app';
import NvTooltip from 'shared/components/nv-tooltip';
import ClickableContainer from 'components/clickable-container';
import { SavingRegistryContext } from 'shared/hooks/use-saving-registry';
import QuestionSetContext,
{
  QuestionContext,
  SavingIndicator,
} from 'lecture_pages/services/question-set-context';
import ResponsivelyEmbeddedAngularHTML from 'shared/components/responsively-embedded-angular-html';
import {
  gray4,
  gray5,
  gray7,
  danger,
  primary,
  success,
  hexToRgbaString,
  black,
} from 'styles/global_defaults/colors';
import {
  halfSpacing,
  largeSpacing,
  threeQuartersSpacing,
} from 'styles/global_defaults/scaffolding';
import useQuestionSetHelpers from 'quizzes/hooks/use-question-set-helpers';
import { useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';
import { LecturePageMode } from 'lecture_pages/components';
import { ComponentType } from 'redux/schemas/models/lecture-component';
import BigCheckbox from './big-checkbox';
import SelectedIndicator from './selected-indicator';

const defaultOptionTemplate = '<p class="froala-style-medium"><br /></p>';

type Props = {
  index: number;
  isDeleteDisabled: boolean;
  option: ResponseOption;
};

const QuizQuestionOption = (props: Props) => {
  const {
    index,
    option,
    isDeleteDisabled,
  } = props;

  const {
    mode,
    lectureComponent,
    canMakeStructuralChanges,
    savingIndicatorTimeoutRef,
    setSavingStatus,
    isHardDeadlineExpired,
    isContentManagementCollection,
  } = React.useContext(QuestionSetContext);

  const { getValues, setValue, trigger } = useFormContext() || {};

  const {
    isEditMode,
    isMultipleAnswer,
    isAnswerMode,
    isCurrentActivityType,
    dataQaQuestionSetAttributes,
  } = useQuestionSetHelpers();

  const {
    answerState,
    setAnswerState,
    currentQuestion,
    areAllOptionsCorrect,
    currentQuestionResponse,
  } = React.useContext(QuestionContext);

  const dispatch = useAppDispatch();
  const mountedRef = React.useRef(false);

  const courseAliases = useSelector(getFlatCourseAliases);
  const feedback = useSelector((state: RootState) => (isCurrentActivityType(ComponentType.PROGRESSIVE_QUIZ)
    ? null
    : getQuestionAnswerFeedback(state, currentQuestion.id, currentQuestion.questionSetId, lectureComponent.type)
  ));
  const [isFocused, setIsFocused] = React.useState(false);
  const angularServices = React.useContext(AngularServicesContext);
  const { registerSaving } = React.useContext(SavingRegistryContext);
  const [isFeedbackFocused, setIsFeedbackFocused] = React.useState(false);
  const [optionContent, setOptionContent] = React.useState(option.optionContent || defaultOptionTemplate);
  const [optionFeedback, setOptionFeedback] = React.useState(option.explanation || defaultOptionTemplate);

  useEffect(() => {
    if (isCurrentActivityType(ComponentType.QUIZ)) {
      if (currentQuestionResponse) {
        setOptionFeedback(feedback.filter((eachFeedback) => eachFeedback.id === option.id)[0]?.explanation);
      }
    }
  }, [
    currentQuestionResponse,
    feedback,
    option.id,
    isCurrentActivityType,
  ]);
  const params = useLecturePageParams();

  const status = currentQuestionResponse?.feedback?.includes(option.id);

  const hasStatus = !!status;

  const isCorrect = () => {
    if (isCurrentActivityType(ComponentType.PROGRESSIVE_QUIZ)) {
      return option.isCorrect;
    }
    if (selected()) {
      return feedback?.filter((eachFeedback) => eachFeedback.id === option.id)[0]?.isCorrect;
    }
    return false;
  };

  const isObject = (variable) => typeof variable === 'object' && variable !== null;
  const selected = () => {
    if (isEditMode) {
      return false;
    }
    // To highlight submitted option response only for multiple choice question
    if (!isMultipleAnswer && currentQuestionResponse) {
      return currentQuestionResponse.response === option.id;
    }
    // To highlight submitted option response for multiple choice multiple answer question
    if (currentQuestionResponse && typeof currentQuestionResponse.response === 'object') {
      return !!(currentQuestionResponse.response as MultiChoiceMultiAnswerQuestionResponse).filter(((optionId) => optionId === option.id)).length;
    }
    if (isCurrentActivityType(ComponentType.PROGRESSIVE_QUIZ)) {
      // To highlight option while selecting in learner mode
      if (isObject(answerState)) {
        return !!answerState[option.id];
      }
      return false;
    }
    if (isMultipleAnswer) {
      if (!getValues(currentQuestion.id.toString())) {
        return false;
      }
      if (!isEmpty((getValues(currentQuestion.id.toString()) as number[]).filter((optionId) => optionId === option.id))) {
        return true;
      }
      return false;
    }
    if (getValues(currentQuestion.id.toString()) === option.id) {
      return true;
    }
    return false;
  };

  const isLecturePageViewMode = params.mode === LecturePageMode.VIEW;

  const editNotFocusedBorderColor = option.isCorrect ? success : gray5;

  const setExplanation = useCallback((explanation: string) => {
    const unregister = registerSaving();
    wrapThunkAction(dispatch(editAnswerFeedback(
      { explanation,
        questionOptionId: option.id },
    ))).then(() => {
      setSavingStatus(SavingIndicator.SUCCESS_STATUS);
    }).catch(() => {
      setSavingStatus(SavingIndicator.ERROR_STATUS);
    }).finally(() => {
      savingIndicatorTimeoutRef.current = setTimeout(() => {
        setSavingStatus(SavingIndicator.HIDDEN_STATUS);
        unregister();
      }, 2000);
    });
  }, [dispatch, option.id, registerSaving, savingIndicatorTimeoutRef, setSavingStatus]);

  const handleChange = React.useCallback((patch: EditQuizQuestionOptionParams['patch']) => {
    clearTimeout(savingIndicatorTimeoutRef.current);
    const unregister = registerSaving();
    setSavingStatus(SavingIndicator.SAVING_STATUS);

    dispatch(editQuizQuestionOption({
      patch,
      id: option.id,
    })).then((res) => {
      if (isEmpty(res.error)) {
        setSavingStatus(SavingIndicator.SUCCESS_STATUS);
      } else {
        setSavingStatus(SavingIndicator.ERROR_STATUS);
      }
    }).finally(() => {
      unregister();
      savingIndicatorTimeoutRef.current = setTimeout(() => {
        setSavingStatus(SavingIndicator.HIDDEN_STATUS);
      }, 2000);
    });
  }, [
    dispatch,
    option.id,
    registerSaving,
    savingIndicatorTimeoutRef,
    setSavingStatus,
  ]);

  const handleDelete = () => {
    const unregister = registerSaving();
    setSavingStatus(SavingIndicator.SAVING_STATUS);
    clearTimeout(savingIndicatorTimeoutRef.current);

    dispatch(deleteQuizQuestionOption(option.id)).then((res) => {
      if (isEmpty(res.error)) {
        setSavingStatus(SavingIndicator.SUCCESS_STATUS);
      } else {
        setSavingStatus(SavingIndicator.ERROR_STATUS);
      }
    }).finally(() => {
      unregister();
      savingIndicatorTimeoutRef.current = setTimeout(() => {
        setSavingStatus(SavingIndicator.HIDDEN_STATUS);
      }, 2000);
    });
  };

  const withStatusColor = () => {
    if (isCurrentActivityType(ComponentType.SURVEY)) {
      return black;
    }
    return isCorrect() ? success : danger;
  };

  const color = hasStatus ? withStatusColor() : primary;

  const notEditSelectedBackgroundColor = isCurrentActivityType(ComponentType.SURVEY) ? gray7 : hexToRgbaString(color, 0.05);

  const isSelectionDisabled = !!currentQuestionResponse;

  React.useEffect(() => {
    if (mountedRef.current) {
      handleChange({ optionContent });
    }
  }, [optionContent, handleChange]);

  React.useEffect(() => {
    if (mountedRef.current && isEditMode) {
      setSavingStatus(SavingIndicator.SAVING_STATUS);
      clearTimeout(savingIndicatorTimeoutRef.current);

      setExplanation(optionFeedback);
    }
  }, [
    optionFeedback,
    savingIndicatorTimeoutRef,
    setSavingStatus,
    isEditMode,
    setExplanation,
  ]);

  React.useEffect(() => {
    mountedRef.current = true;
  }, []);

  const styles = css`
    background-color: white;

    .handle-container {
      width: ${largeSpacing}px;
      left: -${largeSpacing}px;

      .handle {
        visibility: hidden;
      }
    }

    &:hover {
      .handle {
        visibility: visible;
      }
    }

    .option-container {
      flex: 1;
      border-radius: 4px;
      ${isEditMode ? css`
        border: 1px solid ${isFocused ? primary : editNotFocusedBorderColor};
      ` : css`
        background-color: ${selected() ? notEditSelectedBackgroundColor : gray7};
        ${selected() && css`
          border: 1px solid ${color};
        `};
      `};

      .question-option-content {
        flex: 1;

        .fr-element {
          outline: 0!important;
          padding-top: 0!important;

          p:last-child {
            margin-bottom: 0;
          }

          ${isEditMode && option.isCorrect && css`
            color: ${success};
          `};
        }
      }

      .question-option-content-view {
        flex: 1;
      }

      .edit-options {
        top: 0;
        right: 0;
        opacity: 0;
      }

      .answer-checkbox {
        opacity: 0;
      }

      &:hover {
        ${!isContentManagementCollection
           && isAnswerMode
           && !isSelectionDisabled
           && !isHardDeadlineExpired && css`
          .answer-checkbox {
            opacity: 1;
          }

          .big-checkbox-alphabet-character {
            font-weight: bold;
          }
        `};
      }
      &:focus-within, &:hover {
        .edit-options {
          opacity: 1;
        }
      }
    }

    .feedback-container {
      padding-right: ${threeQuartersSpacing}px;
      .feedback-content {
        .fr-element {
          p:last-child {
            margin-bottom: 0;
          }
        }
      }

      .feedback-icon {
        color: ${isCorrect() ? success : danger};
      }

      .feedback-content-container {
        flex: 1;
        min-height: ${largeSpacing}px;
        border: 1px solid transparent;

        ${!option.explanation && css`
          border-color: ${gray4};
          background-color: ${gray7};
        `};

        ${isFeedbackFocused && css`
          border-color: ${primary};
          background-color: transparent;
        `};

        .feedback-content {
          flex: 1;

          .fr-element {
            overflow-y: hidden;
            border: 0!important;
            outline: 0!important;
            padding-top: 0!important;
            padding-bottom: 0!important;
            background-color: transparent!important;
            padding-left: ${halfSpacing}px!important;
            padding-right: ${halfSpacing}px!important;
            color: ${((isEditMode ? (option.isCorrect || areAllOptionsCorrect) : option.isCorrect)) ? success : danger};

            p:last-child {
              margin-bottom: 0;
            }
          }

          .fr-placeholder {
            border: 0!important;
          }
        }
      }
    }
  `;

  const isDeleteOptionDisabled = !canMakeStructuralChanges;

  const defaultFeedback = isCorrect() ? t.SHARED.CORRECT() : t.SHARED.INCORRECT();
  const actualOptionFeedback = isEditMode ? option.explanation : (optionFeedback || defaultFeedback);

  const setMultipleChoiceValue = (value) => {
    setValue(currentQuestion.id.toString(), value);
    trigger(currentQuestion.id.toString());
  };

  const handleOptionSelect = () => {
    if (isCurrentActivityType(ComponentType.PROGRESSIVE_QUIZ)) {
      if (isMultipleAnswer) {
        setAnswerState(
          { ...(typeof answerState === 'object' ? answerState : {}),
            [option.id]: !selected() },
        );
      } else {
        setAnswerState({
          [option.id]: true,
        });
      }
    } else {
      if (isMultipleAnswer) {
        if (!selected()) {
          if (!getValues(currentQuestion.id.toString())) {
            setMultipleChoiceValue([option.id]);
            return;
          }
          setMultipleChoiceValue([...getValues(currentQuestion.id.toString()), option.id]);
          return;
        }
        const newOptions = getValues(currentQuestion.id.toString()).filter((optionId) => optionId !== option.id);
        setMultipleChoiceValue([...newOptions]);
        return;
      }
      if (!selected()) {
        setMultipleChoiceValue(option.id);
      }
    }
  };

  const getAriaLabel = () => {
    if (isMultipleAnswer) {
      if (selected()) {
        return t.QUIZZES.CHECKBOX_CHECKED();
      }
      return t.QUIZZES.CHECKBOX_UNCHECKED();
    }
    if (selected()) {
      return t.QUIZZES.RADIO_BUTTON_SELECTED();
    }
    return t.QUIZZES.RADIO_BUTTON_UNSELECTED();
  };

  const getReleasedLessonDeleteOptionDisabledText = () => {
    switch (lectureComponent.type) {
      case ComponentType.QUIZ:
      case ComponentType.PROGRESSIVE_QUIZ:
        return t.QUIZZES.ALREADY_RELEASED.OPTION_DELETE(courseAliases);
      case ComponentType.SURVEY:
        return t.SURVEYS.ALREADY_RELEASED.OPTION_DELETE(courseAliases);
      default:
        return '';
    }
  };

  return (
    <div css={styles}>
      <div className='d-flex flex-row position-relative'>
        {isEditMode && (
          <div className='handle-container position-absolute h-100 d-flex align-items-center'>
            {/* Reusing this translation to not create a new one */}
            <NvTooltip text={t.LECTURE_PAGES.COMPONENTS.ACCORDION.DRAG_AND_DROP_TO_REORDER()}>
              <ClickableContainer
                className='handle'
                data-qa={dataQaQuestionSetAttributes.questionModal.optionReorder}
                data-qa-id={`${dataQaQuestionSetAttributes.questionModal.optionReorder}_${option.id}`}
                aria-label={t.LECTURE_PAGES.COMPONENTS.ACCORDION.DRAG_AND_DROP_TO_REORDER()}
              >
                <NvIcon icon='drag-handle' size='small' />
              </ClickableContainer>
            </NvTooltip>
          </div>
        )}
        <p
          className='sr-only'
          id={`status-${option.id}`}
        >
          {getAriaLabel()}
        </p>
        <ClickableContainer
          layoutOnly={!isAnswerMode || isHardDeadlineExpired || isContentManagementCollection}
          disabled={isSelectionDisabled}
          onClick={handleOptionSelect}
          className='d-flex align-items-center p-3 option-container'
          data-qa={dataQaQuestionSetAttributes.questionModal.option}
          data-qa-id={`${dataQaQuestionSetAttributes.questionModal.option}_${option.id}`}
          aria-labelledby={`status-${option.id} content-${option.id}`}
        >
          <BigCheckbox
            mode={mode}
            index={index}
            color={color}
            className='mr-2'
            selected={selected()}
            checked={option.isCorrect}
            characterClassName='big-checkbox-alphabet-character'
            onChange={(newCorrect) => handleChange({ isCorrect: newCorrect })}
            optionId={option.id}
            circularBorder={!isMultipleAnswer
              && (isLecturePageViewMode || isCurrentActivityType(ComponentType.SURVEY))}
          />
          {isEditMode ? (
            <NvFroala
              allowToolbar
              value={optionContent}
              minHeight={threeQuartersSpacing}
              onBlur={() => setIsFocused(false)}
              onFocus={() => setIsFocused(true)}
              className='question-option-content'
              placeholder={t.QUIZZES.INPUT_OPTION()}
              editorClass='no-background no-padding'
              onChange={(html) => setOptionContent(html)}
              dataQa={dataQaQuestionSetAttributes.questionModal.optionContent}
              dataQaId={`${dataQaQuestionSetAttributes.questionModal.optionContent}_${option.id}`}
            />
          ) : (
            <div id={`content-${option.id}`} className='question-option-content'>
              <ResponsivelyEmbeddedAngularHTML
                template={option.optionContent ?? ''}
                angularServices={angularServices}
                className='fr-element question-option-content-view'
              />
            </div>
          )}
          {isEditMode && (
            <div className='d-flex align-items-center justify-content-center h-100 ml-2 edit-options'>
              {option.explanation === null && !isCurrentActivityType(ComponentType.SURVEY) && (
              <NvTooltip text={t.QUIZZES.ADD_FEEDBACK(courseAliases)}>
                <ClickableContainer
                  className='mr-2'
                  onClick={() => {
                    setExplanation('');
                    setOptionFeedback('');
                  }}
                    data-qa={dataQaQuestionSetAttributes.questionModal.optionAddHint}
                    data-qa-id={`${dataQaQuestionSetAttributes.questionModal.optionAddHint}_${option.id}`}
                  aria-label={t.QUIZZES.ADD_FEEDBACK(courseAliases)}
                >
                  <NvIcon icon='comments' size='small' className='text-primary' />
                </ClickableContainer>
              </NvTooltip>
              )}
              {!isDeleteDisabled && (
                <NvTooltip text={isDeleteOptionDisabled ? getReleasedLessonDeleteOptionDisabledText() : t.NOVOED.DELETE()}>
                  <ClickableContainer
                    onClick={handleDelete}
                    disabled={isDeleteOptionDisabled}
                    data-qa={dataQaQuestionSetAttributes.questionModal.optionDelete}
                    data-qa-id={`${dataQaQuestionSetAttributes.questionModal.optionDelete}_${option.id}`}
                    aria-label={t.NOVOED.DELETE()}
                  >
                    <NvIcon icon='trash' size='small' className={isDeleteOptionDisabled ? 'gray-4' : 'text-primary'} />
                  </ClickableContainer>
                </NvTooltip>
              )}
            </div>
          )}
          {!isEditMode && (
            <SelectedIndicator
              color={color}
              selected={selected()}
              questionType={currentQuestion.type}
              unselectedClassName='answer-checkbox'
            />
          )}
        </ClickableContainer>
      </div>
      {actualOptionFeedback !== null && !isCurrentActivityType(ComponentType.SURVEY) && (
        <>
          {isEditMode ? (
            <div className='d-flex align-items-center feedback-container mt-2'>
              <div className='d-flex align-items-center feedback-content-container'>
                <NvFroala
                  allowToolbar
                  value={optionFeedback}
                  editorClass='no-padding'
                  className='feedback-content'
                  minHeight={threeQuartersSpacing}
                  onBlur={() => setIsFeedbackFocused(false)}
                  onFocus={() => setIsFeedbackFocused(true)}
                  onChange={(html) => setOptionFeedback(html)}
                  placeholder={(option.isCorrect || areAllOptionsCorrect) ? t.SHARED.CORRECT() : t.SHARED.INCORRECT()}
                  dataQa={dataQaQuestionSetAttributes.questionModal.optionHintContent}
                  dataQaId={`${dataQaQuestionSetAttributes.questionModal.optionHintContent}_${option.id}`}
                />
              </div>
              <NvTooltip text={t.NOVOED.DELETE()}>
                <ClickableContainer
                  className='ml-3'
                  onClick={() => {
                    setSavingStatus(SavingIndicator.SAVING_STATUS);
                    clearTimeout(savingIndicatorTimeoutRef.current);

                    setExplanation(null);
                  }}
                  data-qa={dataQaQuestionSetAttributes.questionModal.optionHintDelete}
                  data-qa-id={`${dataQaQuestionSetAttributes.questionModal.optionHintDelete}_${option.id}`}
                  aria-label={t.NOVOED.DELETE()}
                >
                  <NvIcon icon='trash' size='small' className='text-primary' />
                </ClickableContainer>
              </NvTooltip>
            </div>
          ) : (hasStatus && (
            <div className='d-flex feedback-container mt-2 align-items-center'>
              <div className='pl-1 mr-2'>
                <NvIcon
                  size='small'
                  className='feedback-icon'
                  icon={isCorrect() ? 'success' : 'error'}
                />
              </div>
              <div className='feedback-content'>
                <ResponsivelyEmbeddedAngularHTML
                  className={`fr-element froala-style-medium ${isCorrect() ? 'text-success' : 'text-danger'}`}
                  template={!isEmpty(option.explanation) ? option.explanation : `<p>${defaultFeedback}</p>`}
                  angularServices={angularServices}
                />
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default QuizQuestionOption;
